/**
 * Step 10 — Ministry Map seed — SYNTHESIS variant
 *
 * Spec: specs/onboarding-rebuild.md → Act 2 step 10
 *
 * Mason lock 2026-05-15: surgical V1 — minimum fields needed for the
 * Ministry Map's createPerson() to land. Production /ministry-map page
 * has the full PersonForm (8+ fields) for managing people; here we just
 * SEED one row to anchor the user emotionally + give them a person they're
 * praying for before they exit onboarding.
 *
 * What this screen captures (3 fields):
 *   1. First name (required)
 *   2. Relationship (5 options — family / close friend / small group /
 *      mentor / coworker — covers ~95% of seed cases; the full 9-option
 *      list lives on the real page)
 *   3. Spiritual status (3 traffic-light buttons, default yellow)
 *
 * What it deliberately drops vs production form:
 *   - Last initial, believer toggle (auto-derived from spiritual_status),
 *     tag pills, prayer notes, next step, follow-up date, last contact.
 *     User can fill those later on /ministry-map.
 *
 * Skip button below the primary CTA. Skipping is allowed — this is the
 * SOLE step in onboarding with a skip option (per spec line 85).
 *
 * Side effect on submit: sets `localStorage.ministry_map_disclaimer_ack_v1`
 * so the user doesn't get hit with the privacy disclaimer when they later
 * land on /ministry-map (they already implicitly ack'd by adding here).
 */

import { useEffect, useState } from 'react'
import ContinueButton from '@/components/onboarding/ContinueButton'

const EASE = 'cubic-bezier(0.16, 1, 0.3, 1)'
const STAGGER_MS = 90
const MAX_NAME_CHARS = 60

// 5 most-common relationship types for the seed. Full 9-option list lives
// on the production /ministry-map page. Order matches "intimate → wider"
// so family lands first.
const RELATIONSHIP_OPTIONS = [
  { value: 'family', label: 'Family' },
  { value: 'close_friend', label: 'Close friend' },
  { value: 'small_group', label: 'Small-group member' },
  { value: 'mentor', label: 'Mentor / Mentee' },
  { value: 'coworker', label: 'Coworker' },
]

// 3 traffic-light spiritual status options. Mirrors the production
// PersonForm. `is_believer` is auto-derived: green = true, yellow/red = false.
const SPIRITUAL_OPTIONS = [
  {
    value: 'green',
    label: 'Walking with God',
    sub: 'Strong faith, growing',
    color: 'rgb(74, 167, 102)',
  },
  {
    value: 'yellow',
    label: 'Somewhere in between',
    sub: 'Believing but distant, or open',
    color: 'var(--gold)',
  },
  {
    value: 'red',
    label: 'Far from God',
    sub: 'Not yet believing, or walked away',
    color: 'rgb(199, 99, 99)',
  },
]

function fadeStyle(slot, mounted) {
  return {
    opacity: mounted ? 1 : 0,
    transform: mounted ? 'translateY(0)' : 'translateY(8px)',
    transition: `opacity 700ms ${EASE} ${slot * STAGGER_MS}ms, transform 700ms ${EASE} ${slot * STAGGER_MS}ms`,
    willChange: 'opacity, transform',
  }
}

export default function MinistryMapSeedSynthesis({ onAdd, onSkip }) {
  const [mounted, setMounted] = useState(false)
  const [firstName, setFirstName] = useState('')
  const [relationship, setRelationship] = useState('family')
  const [spiritual, setSpiritual] = useState('yellow')
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    const id = requestAnimationFrame(() => setMounted(true))
    return () => cancelAnimationFrame(id)
  }, [])

  const canSubmit = firstName.trim().length > 0 && !submitting

  async function handleAdd() {
    if (!canSubmit) return
    setSubmitting(true)
    try {
      // Pre-ack the privacy disclaimer for /ministry-map so the user
      // doesn't get hit with it when they land on the real page later.
      try {
        localStorage.setItem('ministry_map_disclaimer_ack_v1', '1')
      } catch {
        /* private mode / quota — non-fatal */
      }
      await onAdd?.({
        first_name: firstName.trim(),
        relationship_type: relationship,
        spiritual_status: spiritual,
        is_believer: spiritual === 'green',
      })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="w-full max-w-md md:max-w-xl lg:max-w-2xl mx-auto px-5 py-10 sm:py-12">
      {/* Heading */}
      <h1
        style={fadeStyle(0, mounted)}
        className="font-heading text-[1.625rem] sm:text-[1.875rem] leading-[1.2] text-[var(--text-primary)] text-center font-semibold"
      >
        Your Ministry Map.
      </h1>
      <p
        style={fadeStyle(1, mounted)}
        className="text-center text-[14px] text-[var(--text-muted)] mt-3 max-w-[30rem] mx-auto leading-relaxed"
      >
        A private map of the people God’s placed in your life — to pray for, share the gospel with,
        and be a light to.
      </p>

      {/* What it is — 3-benefit explainer block (Mason 2026-05-17). Adds
          clarity before the form because most users have never seen a
          "Ministry Map" feature elsewhere. Framing reflects evangelism +
          lifestyle witness, not just intercessory prayer. */}
      <ul
        style={fadeStyle(2, mounted)}
        className="mt-6 mx-auto max-w-[24rem] rounded-xl p-4 space-y-2.5"
      >
        {[
          { icon: '🙏', text: 'Pray for the people on your heart, by name' },
          { icon: '✨', text: 'Plan your next step — text, coffee, a follow-up' },
          { icon: '🔔', text: 'Get nudged when it’s time to reach out' },
        ].map((item, i) => (
          <li
            key={i}
            className="flex items-start justify-center gap-2.5 text-[13.5px] text-[var(--text-primary)] leading-snug text-center"
          >
            <span aria-hidden="true" className="flex-shrink-0 text-[15px] leading-none">
              {item.icon}
            </span>
            <span>{item.text}</span>
          </li>
        ))}
      </ul>

      {/* Form card */}
      <div
        style={{
          ...fadeStyle(2, mounted),
          background: 'var(--bg-surface)',
          borderColor: 'var(--glass-border)',
        }}
        className="mt-8 rounded-2xl p-5 sm:p-6 border space-y-5"
      >
        {/* First name */}
        <div>
          <label
            htmlFor="mms-first-name"
            className="block text-[10px] uppercase tracking-[0.22em] text-[var(--text-muted-soft)] font-semibold mb-2"
          >
            First name
          </label>
          <input
            id="mms-first-name"
            type="text"
            value={firstName}
            onChange={e => setFirstName(e.target.value.slice(0, MAX_NAME_CHARS))}
            placeholder="Mom, Sarah, Coach Mike…"
            className="w-full px-4 py-3 rounded-md text-[14.5px]"
            style={{
              background: 'var(--bg-surface-high)',
              border: '1px solid var(--glass-border)',
              color: 'var(--text-primary)',
              fontFamily: 'var(--font-body)',
              outline: 'none',
              transition: `border-color 180ms ${EASE}`,
            }}
            onFocus={e => (e.currentTarget.style.borderColor = 'var(--gold)')}
            onBlur={e => (e.currentTarget.style.borderColor = 'var(--glass-border)')}
            autoComplete="off"
          />
        </div>

        {/* Relationship */}
        <div>
          <label
            htmlFor="mms-relationship"
            className="block text-[10px] uppercase tracking-[0.22em] text-[var(--text-muted-soft)] font-semibold mb-2"
          >
            Relationship
          </label>
          <select
            id="mms-relationship"
            value={relationship}
            onChange={e => setRelationship(e.target.value)}
            className="w-full px-4 py-3 rounded-md text-[14.5px]"
            style={{
              background: 'var(--bg-surface-high)',
              border: '1px solid var(--glass-border)',
              color: 'var(--text-primary)',
              fontFamily: 'var(--font-body)',
              outline: 'none',
              appearance: 'none',
              backgroundImage:
                "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12' fill='none'><path d='M3 4.5l3 3 3-3' stroke='%23888' stroke-width='1.4' stroke-linecap='round' stroke-linejoin='round'/></svg>\")",
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'right 14px center',
              paddingRight: '36px',
            }}
          >
            {RELATIONSHIP_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        {/* Spiritual status — 3 traffic-light buttons */}
        <div>
          <p className="block text-[10px] uppercase tracking-[0.22em] text-[var(--text-muted-soft)] font-semibold mb-2">
            Where are they spiritually?
          </p>
          <div className="space-y-2">
            {SPIRITUAL_OPTIONS.map(opt => {
              const selected = spiritual === opt.value
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setSpiritual(opt.value)}
                  className="w-full flex items-center gap-3 px-3.5 py-2.5 rounded-md text-left"
                  style={{
                    background: selected ? 'var(--gold-select)' : 'var(--bg-surface-high)',
                    border: `1px solid ${selected ? 'var(--gold)' : 'var(--glass-border)'}`,
                    color: 'var(--text-primary)',
                    fontFamily: 'var(--font-body)',
                    transition: `background 180ms ${EASE}, border-color 180ms ${EASE}`,
                    cursor: 'pointer',
                  }}
                >
                  <span
                    aria-hidden
                    style={{
                      width: 12,
                      height: 12,
                      borderRadius: '50%',
                      background: opt.color,
                      flexShrink: 0,
                      boxShadow: selected ? `0 0 8px ${opt.color}` : 'none',
                    }}
                  />
                  <span className="flex-1 min-w-0">
                    <span className="block text-[14px] font-semibold leading-tight">
                      {opt.label}
                    </span>
                    <span className="block text-[12px] text-[var(--text-muted)] mt-0.5">
                      {opt.sub}
                    </span>
                  </span>
                </button>
              )
            })}
          </div>
        </div>
      </div>

      {/* CTA — Add this person + Skip */}
      <div style={fadeStyle(4, mounted)} className="mt-7 flex flex-col items-center gap-4">
        <ContinueButton onClick={handleAdd} disabled={!canSubmit}>
          {submitting ? 'Adding…' : 'Add this person'}
        </ContinueButton>
        <button
          type="button"
          onClick={onSkip}
          className="text-[13px] text-[var(--text-muted)] hover:text-[var(--text-primary)]"
          style={{
            background: 'transparent',
            border: 0,
            cursor: 'pointer',
            padding: '6px 12px',
            transition: `color 180ms ${EASE}`,
            fontFamily: 'var(--font-body)',
          }}
        >
          Skip for now →
        </button>
      </div>
    </div>
  )
}
